import { api } from './client';
import type { MemberRole } from './members';

// Backend only knows active/archived (contracts/db enums) — a request with
// 'deleted' here would be rejected by the Zod DTO on the backend.
export type ProjectStatus = 'active' | 'archived';

export interface Project {
  id: string;
  public_id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  owner_company_id: string;
  bios_prefixes: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateProjectDto {
  name: string;
  description?: string;
  owner_company_id: string;
  /** Created as a `lead` member in the same transaction as the project. */
  lead_user_id?: string;
  /** Other people who join with the project, each with their own role. */
  members?: { user_id: string; role: MemberRole }[];
}

export interface UpdateProjectDto {
  name?: string;
  description?: string | null;
  bios_prefixes?: string[];
}

export interface ListProjectsResponse {
  items: Project[];
  next_cursor: string | null;
}

export const projectsApi = {
  list: (params: { status?: ProjectStatus; cursor?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit) search.set('limit', String(params.limit));
    return api.get<ListProjectsResponse>(`/projects?${search.toString()}`).then((r) => r.data);
  },

  get: (id: string) => api.get<Project>(`/projects/${id}`).then((r) => r.data),

  create: (dto: CreateProjectDto) => api.post<Project>('/projects', dto).then((r) => r.data),

  update: (id: string, dto: UpdateProjectDto) =>
    api.patch<Project>(`/projects/${id}`, dto).then((r) => r.data),

  remove: (id: string) => api.delete(`/projects/${id}`).then(() => undefined),

  archive: (id: string) => api.post<Project>(`/projects/${id}/archive`).then((r) => r.data),

  unarchive: (id: string) => api.post<Project>(`/projects/${id}/unarchive`).then((r) => r.data),
};
